import type { ReactNode } from 'react';
import {
    type IpgConfig,
    type IpgConfigCustomization,
    type IpgSystemId,
    type IpgSystemIdKey,
    type MerchantConfig,
} from '@anchorbase/pillar-sdk';

export interface IpgConfigEditorValidationIssue {
    readonly fieldLabel: string;
    readonly reason: string;
    readonly anchor: string;
}

export interface IpgConfigEditorAdapterCapabilities {
    readonly supportsEditableConfig: boolean;
    readonly supportsReadOnlySummary: boolean;
    readonly supportsDefaultCredentialPlaceholders: boolean;
}

export const IpgPaymentFeatureCapabilityState: {
    readonly SUPPORTED: 'SUPPORTED';
    readonly UNSUPPORTED: 'UNSUPPORTED';
    readonly DEFERRED: 'DEFERRED';
} = {
    SUPPORTED: 'SUPPORTED',
    UNSUPPORTED: 'UNSUPPORTED',
    DEFERRED: 'DEFERRED',
};

export type IpgPaymentFeatureCapabilityState =
    (typeof IpgPaymentFeatureCapabilityState)[keyof typeof IpgPaymentFeatureCapabilityState];

export interface IpgPaymentFeatureCapabilityDisplayRule {
    readonly featureId: string;
    readonly label: string;
    readonly state: IpgPaymentFeatureCapabilityState;
    readonly tooltip: string;
}

export function formatIpgPaymentFeatureCapabilityStateLabel(
    state: IpgPaymentFeatureCapabilityState
): string {
    switch (state) {
        case IpgPaymentFeatureCapabilityState.SUPPORTED:
            return 'Configured';
        case IpgPaymentFeatureCapabilityState.UNSUPPORTED:
            return 'Unavailable';
        case IpgPaymentFeatureCapabilityState.DEFERRED:
            return 'Review required';
        default: {
            const exhaustiveState: never = state;
            throw new Error(
                `Unknown IPG payment feature capability state: ${exhaustiveState}`
            );
        }
    }
}

export interface IpgConfigEditorAdapterState {
    readonly ipgSystemId: IpgSystemId;
    readonly ipgSystemIdKey: IpgSystemIdKey;
}

export interface IpgConfigEditorCreateStateInput {
    readonly merchantConfig: MerchantConfig | undefined;
    readonly defaultConfig: IpgConfig;
    readonly demoConfig: IpgConfig | undefined;
}

export interface IpgConfigEditorValidationInput<
    TEditorState extends IpgConfigEditorAdapterState,
> {
    readonly editorState: TEditorState;
}

export interface IpgConfigEditorSavePayloadInput<
    TEditorState extends IpgConfigEditorAdapterState,
> {
    readonly editorState: TEditorState;
}

export interface IpgConfigEditorEditableRenderInput<
    TEditorState extends IpgConfigEditorAdapterState,
> {
    readonly organizationUuid: string;
    readonly editorState: TEditorState;
    readonly onChange: (nextState: TEditorState) => void;
    readonly disabled: boolean;
}

export interface IpgConfigEditorReadOnlySummaryInput {
    readonly organizationUuid: string;
    readonly merchantConfig: MerchantConfig | undefined;
    readonly defaultConfig: IpgConfig;
}

export interface IpgConfigEditorAdapter<
    TEditorState extends IpgConfigEditorAdapterState =
        IpgConfigEditorAdapterState,
    TPayload extends IpgConfigCustomization = IpgConfigCustomization,
> {
    readonly ipgSystemId: IpgSystemId;
    readonly ipgSystemIdKey: IpgSystemIdKey;
    readonly label: string;
    readonly capabilities: IpgConfigEditorAdapterCapabilities;
    createEditableState(input: IpgConfigEditorCreateStateInput): TEditorState;
    validateEditableState(
        input: IpgConfigEditorValidationInput<TEditorState>
    ): readonly IpgConfigEditorValidationIssue[];
    buildSavePayload(
        input: IpgConfigEditorSavePayloadInput<TEditorState>
    ): TPayload;
    renderEditable(
        input: IpgConfigEditorEditableRenderInput<TEditorState>
    ): ReactNode;
    renderReadOnlySummary(input: IpgConfigEditorReadOnlySummaryInput): ReactNode;
}
